<script lang="ts">
  import ErrorWarningAlertIcon from '$icons/ErrorWarningAlertIcon.svelte';
  import { cn } from '$lib/utils/misc';
  import type { HTMLInputTypeAttribute } from 'svelte/elements';
  import { twMerge } from 'tailwind-merge';

  // Core input props
  export let value: string | number | null = '';
  export let type: HTMLInputTypeAttribute = 'text';
  export let name = '';
  export let id = '';
  export let placeholder = '';
  export let disabled = false;
  export let autocomplete = 'off';
  export let input: HTMLInputElement | null = null;

  // Label and validation props
  export let label = '';
  export let description = '';
  export let optional = false;
  export let required = false;
  export let recommended = false;
  export let errors: string[] = [];

  // Styling props
  export let labelClass = '';
  export let inputClass = '';

  let leftIcon: HTMLDivElement;
  let rightIcon: HTMLDivElement;
</script>

<!-- TODO add emphasized -->
<div
  class={twMerge(
    'font-inter flex w-full max-w-full flex-col items-start text-xs font-normal leading-4',
    $$props.class,
  )}
>
  <label for={id} class="">
    {#if label}
      <p
        class={cn(
          'font-architekt text-gray-dark-12 mb-2 text-sm font-normal leading-5',
          labelClass,
        )}
      >
        {label}
        {#if optional}
          <span class="text-gray-dark-10 text-[12px]">(Optional)</span>
        {/if}
        {#if recommended}
          <span class="text-[12px] text-amber-500">(Recommended)</span>
        {/if}
        {#if required}
          <span class="text-red-dark-10 text-[12px]">*</span>
        {/if}
      </p>
    {/if}
    {#if description}
      <p class="text-gray-dark-11 mb-2 mt-1 text-xs">
        {@html description}
      </p>
    {/if}
  </label>
  <div class={cn('text-gray-dark-8 relative h-10 w-full rounded', inputClass)}>
    <div bind:this={leftIcon} class="absolute left-4 top-3 h-4 w-4">
      <slot name="left-icon" />
    </div>
    <div bind:this={rightIcon} class="absolute right-4 top-3 h-4 w-4">
      <slot name="right-icon" />
    </div>
    {#if type == 'text'}
      <input
        {disabled}
        type="text"
        {name}
        {placeholder}
        {id}
        {autocomplete}
        bind:value
        on:focus
        on:blur
        on:keyup
        on:keydown
        on:change
        class:errors={errors?.length > 0}
        class={cn(
          'pl-4 pr-4',
          leftIcon?.hasChildNodes() ? 'pl-[38px]' : '',
          rightIcon?.hasChildNodes() ? 'pr-[38px]' : '',
        )}
      />
    {:else if type == 'password'}
      <input
        bind:this={input}
        {disabled}
        type="password"
        {name}
        {placeholder}
        {id}
        {autocomplete}
        bind:value
        on:focus
        on:blur
        on:keyup
        on:keydown
        on:change
        class:errors={errors?.length > 0}
        class="{leftIcon?.hasChildNodes() ? 'pl-[38px]' : 'pl-4'} {rightIcon?.hasChildNodes()
          ? 'pr-[38px]'
          : 'pr-4'}"
      />
    {/if}
  </div>
  {#if errors?.length > 0}
    {#each errors as error}
      {#if error}
        <div class="text-red-dark-10 mt-2 flex items-start justify-start gap-x-1 gap-y-0.5">
          <ErrorWarningAlertIcon class="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p class="text-red-dark-10">{error}</p>
        </div>
      {/if}
    {/each}
  {/if}
</div>

<style lang="postcss">
  .errors {
    @apply border-red-dark-10;
  }
  input {
    @apply border-gray-dark-4 text-gray-dark-12 caret-mint-dark-11 h-full w-full rounded border bg-transparent outline-none ring-[3px] ring-transparent;
  }
  input:disabled {
    @apply bg-gray-dark-3 text-gray-dark-11 cursor-not-allowed;
  }
  input::placeholder {
    @apply text-gray-dark-8;
  }
  input:focus {
    @apply border-mint-dark-8 ring-mint-dark-4;
  }
  input:hover {
    @apply border-gray-dark-5;
  }
</style>
